import React, { useCallback, useMemo } from 'react';
import { Table, TableData, TableRow } from '@cokitchen/cokitchen-components';
import { DeleteIcon, EditAltIcon, RmxCheckIcon } from 'cokitchen-icons';
import EditableInput from 'components/base/editable-input';
import SelectDropdown from 'components/base/select-dropdown';
import { storeItemWeightMeasurement } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';
import useInventoryStoreContext from 'contexts/inventory-store.context/context';

type ItemDetailsTabProps = {
  editingItemIndex: number | null;
  onItemInputChange: (index: number, field: string, value: string) => void;
  onEditItem: (index: number) => void;
  onSaveItem: () => void;
  onDeleteItem: (index: number) => void;
};

const ItemDetailsTab: React.FC<ItemDetailsTabProps> = ({
  editingItemIndex,
  onItemInputChange,
  onEditItem,
  onSaveItem,
  onDeleteItem
}) => {
  const { storeItemArray } = useInventoryStoreContext();
  const { formIngredientCategoryList } = useDashboardContext();
  const unitOptions = useMemo(() => {
    return storeItemWeightMeasurement.map((unit) => ({
      id: unit.value,
      name: unit.name
    }));
  }, []);

  const categoryOptions = useMemo(() => {
    return formIngredientCategoryList.map((category) => ({
      id: category,
      name: category
    }));
  }, [formIngredientCategoryList]);

  const isItemEditing = useCallback(
    (index: number): boolean => {
      return editingItemIndex === index;
    },
    [editingItemIndex]
  );

  const ITEM_DETAILS_HEADERS = [
    'Item Name',
    'Unit of Meas.',
    'Unit per g',
    'Category',
    'Min. Qty',
    'Shelf Life (Days)',
    'Vendors',
    'Actions'
  ];

  return (
    <Table headers={ITEM_DETAILS_HEADERS}>
      {storeItemArray.length === 0
        ? [
            <TableRow key='empty'>
              <TableData className='text-center py-8 text-gray-400 col-span-8'>
                No store items added yet
              </TableData>
            </TableRow>
          ]
        : storeItemArray.map((storeItem, index) => (
            <TableRow key={index} className='hover:bg-gray-50 transition-colors'>
              <TableData className='px-2 py-2 text-sm text-gray-800'>
                <EditableInput
                  isEditing={isItemEditing(index)}
                  showError
                  value={storeItem.name}
                  type='text'
                  onChange={(e) => onItemInputChange(index, 'name', e.target.value)}
                />
              </TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                <div className={storeItem.min_quantity_unit ? '' : 'border border-red-5'}>
                  {isItemEditing(index) ? (
                    <SelectDropdown
                      placeholder='Select unit'
                      value={storeItem.min_quantity_unit}
                      setValue={(unit) => onItemInputChange(index, 'min_quantity_unit', unit.id)}
                      options={unitOptions}
                    />
                  ) : (
                    <span className='capitalize'>{storeItem.min_quantity_unit}</span>
                  )}
                </div>
              </TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                <EditableInput
                  isEditing={isItemEditing(index)}
                  value={storeItem.unit?.toString() || ''}
                  showError
                  type='number'
                  onChange={(e) => onItemInputChange(index, 'unit', e.target.value)}
                />
              </TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                <div className={storeItem.category ? '' : 'border border-red-5'}>
                  {isItemEditing(index) ? (
                    <SelectDropdown
                      placeholder='Select category'
                      value={storeItem.category}
                      setValue={(category) => onItemInputChange(index, 'category', category.name)}
                      options={categoryOptions}
                    />
                  ) : (
                    <span>{storeItem.category || '-'}</span>
                  )}
                </div>
              </TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                <EditableInput
                  isEditing={isItemEditing(index)}
                  showError
                  value={storeItem.quantity}
                  type='text'
                  onChange={(e) => onItemInputChange(index, 'quantity', e.target.value)}
                />
              </TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                <EditableInput
                  isEditing={isItemEditing(index)}
                  value={storeItem.shelfLife?.toString() || ''}
                  showError
                  type='number'
                  onChange={(e) => onItemInputChange(index, 'shelfLife', e.target.value)}
                />
              </TableData>

              <TableData className='px-2 py-2 text-sm text-gray-800'>
                {storeItem.vendors.length > 0 ? (
                  <h3>
                    {storeItem.vendors[0]?.name}
                    {storeItem.vendors.length > 1 && (
                      <span className='border border-gray-3 ml-1 p-1'>
                        {' '}
                        +{storeItem.vendors.length - 1}
                      </span>
                    )}
                  </h3>
                ) : (
                  <h2 className='border border-red-5 min-w-[100px] p-2'>No vendor</h2>
                )}
              </TableData>

              <TableData className='px-2 py-2 text-sm'>
                <div className='flex items-center gap-3'>
                  {isItemEditing(index) ? (
                    <div className='cursor-pointer text-green-6' onClick={onSaveItem}>
                      <RmxCheckIcon />
                    </div>
                  ) : (
                    <div className='cursor-pointer text-blue-5' onClick={() => onEditItem(index)}>
                      <EditAltIcon />
                    </div>
                  )}
                  <div className='cursor-pointer text-red-5' onClick={() => onDeleteItem(index)}>
                    <DeleteIcon />
                  </div>
                </div>
              </TableData>
            </TableRow>
          ))}
    </Table>
  );
};

export default ItemDetailsTab;
